<template>
  <avs-card>
    <template v-slot:title>
      <v-toolbar
        class="mt-4 custom-toolbar"
        elevation="0"
      >
        <v-text-field
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          v-model="search"
        />
        <slot
          name="actions"
          v-bind:dataset="dataset"
        />
      </v-toolbar>
    </template>
    <template v-slot:content>
      <v-data-table
        class="ma-10"
        disable-pagination
        :no-data-text="$t('messages.information.no_data')"
        :headers="headers"
        hide-default-footer
        :items="dataset"
        :loading="loading"
        loading-text="Chargements des clients"
        no-results-text="$t('messages.information.no_data')"
        :search="search"
        @dblclick:row="($event, { item }) => toEdit(item)"
      >
        <template #item.start_date="{ item }">
          {{ toDatetime(item.start_date) }}
        </template>
        <template #item.end_date="{ item }">
          {{ toDatetime(item.end_date) }}
        </template>
        <template #item.reservations_count="{ item }">
          <v-chip
            small
            :color="item.reservations_count > 0 ? 'primary' : 'grey'"
            text-color="white"
          >
            {{ item.reservations_count }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <!-- Bouton Éditer -->
            <avs-editButton
              :to="getToEditLink(item)"
              :spantext="
                $t('actions.edit', { entity: $tc('categories.inspection.name', 1).toLowerCase() })
              "
            />

            <!-- Bouton Supprimer avec tooltip -->
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                >
                  <avs-deleteButton
                    @click="handleOpenDeleteDialog(item)"
                    :disabled="hasReservations(item)"
                    :spantext="
                      $t('actions.delete', {
                        entity: $tc('categories.inspection.name', 1).toLowerCase()
                      })
                    "
                  />
                </span>
              </template>
              <span v-if="hasReservations(item)">
                {{ $t("Impossible de supprimer - ce client a") }} {{ item.reservations_count }}
                {{ $t("réservation(s)") }}
              </span>
              <span v-else>
                {{ $t("Supprimer le client") }}
              </span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
      <avs-deleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleCancel"
        @delete="handleDelete"
        resource="Client"
      />
    </template>
  </avs-card>
</template>

<script>
import { CustomerService } from "@/services"
import { DocumentService, TemplateService } from "@/services/documents"
import { toDatetime } from "@/utils/datetime"
import { injectValues } from "@/utils/datamodels"
import { handleExportCSV, fmtTemplate, fmtValues } from "@/utils/download"

export default {
  name: "CustomersTable",
  computed: {
    project() {
      return this.$store.state.app.project
    }
  },
  data() {
    return {
      dataset: [],
      deleteDialogIsOpen: false,
      document: null,
      headers: [
        { text: this.$t("Nom"), value: "first_name", class: "min-width-200" },
        { text: this.$t("Prenom"), value: "last_name", class: "min-width-150" },
        { text: this.$t("Email"), value: "email", class: "min-width-150" },
        { text: this.$t("N° Téléphone"), align: "center", value: "phone_number" },
        {
          text: this.$t("Réservations"),
          align: "center",
          value: "reservations_count",
          sortable: true
        },
        {
          text: this.$t("commons.actions"),
          sortable: false,
          value: "actions",
          align: "right",
          width: 200
        }
      ],
      loading: false,
      search: "",
      selectedItem: null,
      template: null,
      values: null,
      duplicate_document: null,
      duplicate_inspection: null,
      duplicate_values: null
    }
  },
  methods: {
    toDatetime,

    /**
     * Vérifie si le client a des réservations
     */
    hasReservations(item) {
      return item.reservations_count && item.reservations_count > 0
    },

    getDataset() {
      this.loading = true
      CustomerService.get(this, this.project.id)
        .then(response => {
          this.dataset = response.results
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
        .finally(() => {
          this.loading = false
        })
    },

    getLinkToMeeting(item) {
      return {
        name: "InspectionMeeting",
        params: { id: item.id, uuid: item.uuid }
      }
    },

    getToEditLink(item) {
      return {
        name: "CustomerCreateUpdate",
        params: { id: item.id }
      }
    },

    handleOpenDeleteDialog(item) {
      // Vérifier si le client a des réservations avant d'ouvrir la modal
      if (this.hasReservations(item)) {
        this.$store.commit("app/message", {
          msg: this.$t(
            "Impossible de supprimer ce client car il a {count} réservation(s) associée(s)",
            {
              count: item.reservations_count
            }
          ),
          color: "warning"
        })
        return
      }

      this.deleteDialogIsOpen = true
      this.selectedItem = item
    },

    handleCancel() {
      this.deleteDialogIsOpen = false
      this.selectedItem = null
    },

    handleDelete() {
      CustomerService.remove(this, this.selectedItem.id)
        .then(r => {
          this.dataset = this.dataset.filter(nc => nc.id !== this.selectedItem.id)
          this.$store.commit("app/message", {
            msg: this.$t("messages.success.deleted", {
              entity: this.$tc("Client")
            }),
            color: "success"
          })
          this.deleteDialogIsOpen = false
          this.selectedItem = null
        })
        .catch(err => {
          let errorMsg = this.$t("Erreur lors de la suppression du client")

          // Récupérer le message d'erreur du backend
          if (err.response && err.response.data) {
            if (err.response.data.error) {
              errorMsg = err.response.data.error
            } else if (err.response.data.reservations_count) {
              errorMsg = this.$t(
                "Impossible de supprimer ce client car il a {count} réservation(s) associée(s)",
                { count: err.response.data.reservations_count }
              )
            }
          }

          this.$store.commit("app/message", {
            msg: errorMsg,
            color: "error"
          })
          this.deleteDialogIsOpen = false
          this.selectedItem = null
        })
    },

    async exportValues(item) {
      const format = "csv"
      CustomerService.document
        .get(this, item.id)
        .then(async document => {
          const template = await TemplateService.details.get(document.template.id)
          const valuesD = await DocumentService.values.getAll(document.id)
          this.document = injectValues(template, valuesD)
          let templateFMT = fmtTemplate(this.document)
          let valuesFMT = fmtValues(this.document)
          handleExportCSV([templateFMT, valuesFMT], format)
        })
        .catch(message => {
          this.$store.commit("app/message", { msg: message, color: "warning" })
        })
    },

    toEdit(item) {
      this.$router.push(this.getToEditLink(item))
    }
  },
  mounted() {
    this.getDataset()
  }
}
</script>

<style scoped>
.custom-toolbar >>> .v-toolbar__content {
  gap: 1rem;
}
</style>
